/**
 * VIRGINIA SOL READING DATA — ANNUAL REFRESH INSTRUCTIONS
 * VDOE releases SOL results annually in late August (typically Aug 27–30).
 * To update:
 *   1. Go to: doe.virginia.gov/data-policy-funding/data-reports/statistics-reports/sol-test-pass-rates-other-results
 *   2. Use the "build-a-table" tool: Year = [new year], Subject = English Reading,
 *      Student Group = All Students, Level = Division.
 *   3. Export to CSV and update VA_SOL_READING_2025 below.
 *   4. Update VA_DATA_SCHOOL_YEAR and VA_DATA_SOURCE_RELEASE_DATE below.
 *   5. RIGOR STANDARD CHANGE — IMPORTANT:
 *        VDOE raised SOL cut scores in September 2025, effective spring 2026
 *        testing (SY 2025-26 data). When you update this file to a 2025-26 or
 *        later dataset, FLIP `VA_NEW_RIGOR_STANDARD` below to `true`. That
 *        single toggle will:
 *          - Show a "New rigor standard" badge in the VA legend section
 *          - Append a clarifying note inside each ward tooltip
 *          - Add a sub-label on the filter-panel toggle
 *        so viewers don't misread the year-over-year drop as a real decline.
 *        Set it back to `false` once enough years have passed that the new
 *        standard is the baseline (BSP team's discretion).
 * Official School Quality Profiles (division-level verification):
 *   schoolquality.virginia.gov
 *
 * Source: VDOE 2024–2025 Standards of Learning results (released August 27, 2025).
 * Statewide average: 74%.
 *
 * IMPORTANT: Virginia has 39 independent cities with separate FIPS from
 * surrounding counties. Each city is its own school division.
 */

/** School year of the dataset currently loaded. Update annually. */
export const VA_DATA_SCHOOL_YEAR = "2024–25";

/** Flip to `true` when this file is updated to a 2025-26 (or later) dataset. */
export const VA_NEW_RIGOR_STANDARD = false;

/** Short label shown in the badge when the rigor flag is on. */
export const VA_RIGOR_BADGE_LABEL = "New rigor standard";

/** Long-form tooltip / legend note explaining the rigor change. */
export const VA_RIGOR_CHANGE_NOTE =
  "VDOE raised SOL cut scores in September 2025, effective spring 2026 testing. " +
  "Pass rates from " + VA_DATA_SCHOOL_YEAR + " are NOT directly comparable to prior " +
  "years — a lower number does not necessarily mean fewer students are reading well.";

export const VA_SOL_READING_2025 = {
  // ── Northern Virginia (DC suburbs)
  "51610": { division: "Falls Church City",    read_pct: 92, region: "Northern Virginia", is_city: true },
  "51107": { division: "Loudoun County",       read_pct: 85, region: "Northern Virginia" },
  "51013": { division: "Arlington County",     read_pct: 84, region: "Northern Virginia" },
  "51061": { division: "Fauquier County",      read_pct: 80, region: "Northern Virginia" },
  "51059": { division: "Fairfax County",       read_pct: 79, region: "Northern Virginia", largest: true },
  "51179": { division: "Stafford County",      read_pct: 78, region: "Northern Virginia" },
  "51153": { division: "Prince William County",read_pct: 76, region: "Northern Virginia" },
  "51177": { division: "Spotsylvania County",  read_pct: 76, region: "Northern Virginia" },
  "51510": { division: "Alexandria City",      read_pct: 72, region: "Northern Virginia", is_city: true },
  "51683": { division: "Manassas City",        read_pct: 68, region: "Northern Virginia", is_city: true },
  "51685": { division: "Manassas Park City",   read_pct: 66, region: "Northern Virginia", is_city: true },

  // ── Hampton Roads
  "51810": { division: "Virginia Beach City",  read_pct: 76, region: "Hampton Roads", is_city: true },
  "51650": { division: "Hampton City",         read_pct: 68, region: "Hampton Roads", is_city: true },
  "51710": { division: "Norfolk City",         read_pct: 63, region: "Hampton Roads", is_city: true },
  "51700": { division: "Newport News City",    read_pct: 66, region: "Hampton Roads", is_city: true },
  "51550": { division: "Chesapeake City",      read_pct: 75, region: "Hampton Roads", is_city: true },
  "51800": { division: "Suffolk City",         read_pct: 72, region: "Hampton Roads", is_city: true },
  "51740": { division: "Portsmouth City",      read_pct: 58, region: "Hampton Roads", is_city: true },

  // ── Central Virginia / Richmond Metro
  "51041": { division: "Chesterfield County",  read_pct: 76, region: "Richmond Metro" },
  "51087": { division: "Henrico County",       read_pct: 74, region: "Richmond Metro" },
  "51760": { division: "Richmond City",        read_pct: 53, region: "Richmond Metro", is_city: true },
  "51730": { division: "Petersburg City",      read_pct: 50, region: "Southside VA", is_city: true },
  "51036": { division: "Charles City County",  read_pct: 62, region: "Richmond Metro" },

  // ── Charlottesville / Piedmont / Shenandoah Valley
  "51003": { division: "Albemarle County",     read_pct: 76, region: "Piedmont" },
  "51540": { division: "Charlottesville City", read_pct: 67, region: "Piedmont", is_city: true },
  "51163": { division: "Rockbridge County",    read_pct: 72, region: "W. Central VA" },
  "51015": { division: "Augusta County",       read_pct: 75, region: "Shenandoah Valley" },
  "51660": { division: "Harrisonburg City",    read_pct: 65, region: "Shenandoah Valley", is_city: true },
  "51840": { division: "Winchester City",      read_pct: 66, region: "Shenandoah Valley", is_city: true },

  // ── Southside / Rappahannock
  "51590": { division: "Danville City",        read_pct: 55, region: "Southside VA", is_city: true },
  "51143": { division: "Prince Edward County", read_pct: 62, region: "Southside VA" },
  "51083": { division: "Halifax County",       read_pct: 60, region: "Southside VA" },
  "51025": { division: "Brunswick County",     read_pct: 58, region: "Southside VA" },
  "51570": { division: "Colonial Heights City",read_pct: 72, region: "Southside VA", is_city: true },
  "51049": { division: "Cumberland County",    read_pct: 60, region: "Southside VA" },
  "51630": { division: "Fredericksburg City",  read_pct: 64, region: "Rappahannock Area", is_city: true },
  "51033": { division: "Caroline County",      read_pct: 68, region: "Rappahannock Area" },
  "51137": { division: "Orange County",        read_pct: 74, region: "Rappahannock Area" },
};

export const VA_STATEWIDE_PCT = 74;

export const VA_SOL_SOURCE = "VA SOL SY 2024–25";

export const VA_SOL_SOURCE_LONG = "Virginia Department of Education — SOL Reading Pass Rate, SY 2024-2025 (released Aug 27, 2025)";

export const VA_SOL_RIGOR_NOTE =
  "2024–25 SOL tests used more rigorous ELA standards — students needed to demonstrate " +
  "30–40% more content knowledge than prior years to pass.";

export const VA_METRIC_FOOTNOTE =
  "Virginia data is the percentage of students who passed the SOL Reading assessment (all " +
  "students, all tested grades). DC and Maryland data reflect the percentage scoring at or " +
  "above proficient on their respective state assessments. All data from SY 2024–25.";

/**
 * Virginia-specific bucket scale. Statewide pass rate (74%) is much higher
 * than MD's MCAP (50.8%), so breakpoints shift accordingly while still anchoring
 * the deepest red (#C62828) to <55% — matching the urgency tier used for
 * Baltimore City and Prince George's County in the MD layer.
 */
export function colorForVaSol(pct) {
  if (pct == null || Number.isNaN(pct)) return "#E8E8E8"; // gray = data unavailable
  if (pct >= 85) return "#FFEDED";
  if (pct >= 75) return "#FFBDBD";
  if (pct >= 65) return "#FF8A8A";
  if (pct >= 55) return "#FF5252";
  return "#C62828";
}

export const VA_SOL_SR_DESCRIPTION =
  "Map showing Virginia school divisions shaded by 2024-25 SOL Reading pass rates. " +
  "Darker red indicates lower pass rates. Falls Church City (92%), Loudoun (85%), and " +
  "Arlington (84%) are highest. Petersburg (50%), Richmond City (53%), and Danville (55%) " +
  "are lowest. Divisions without published data appear in gray.";

/** Approximate Virginia bounding box — used to auto-enable the layer when the
 *  viewport center sits inside Virginia. */
export const VA_BOUNDS = {
  south: 36.54,
  west: -83.68,
  north: 39.47,
  east: -75.24,
};
